<template>
  <Panel
    :id="id"
    class="quick-quote-stop-edit elevated flex flex-column"
    :class="{
      origin: isPickup,
      destination: !isPickup,
    }"
    tabindex="-1"
  >
    <template #header>
      <div class="shipment-stop-header">
        <div class="shipment-stop-header-label">
          <PickupIcon v-if="isPickup" />
          <DropoffIcon v-else />
          <span
            v-text="isPickup ? t('orders.origin') : t('orders.destination')"
          />
        </div>

        <div class="shipment-stop-header-address">
          <div class="flex flex-gap-1 align-items-center">
            <a
              v-if="isFiconLocation && hasLocationUpdate"
              :href="
                generateLocationLink(
                  internalValue.location,
                  selectedDomain?.naturalId,
                )
              "
              class="location-link"
            >
              {{ internalValue.location.locationName }}
            </a>
            <span v-else class="font-normal text-gray-darkest">
              {{
                isFiconLocation
                  ? internalValue.location?.locationName
                  : t('common.newParam', { param: t('locations.locations') })
              }}
            </span>

            <template
              v-if="
                internalValue.location.addressLine1 ||
                internalValue.location.cityStatePostalCode
              "
            >
              <span>-</span>

              <span v-if="internalValue.location.addressLine1">
                {{ internalValue.location.addressLine1 }}
              </span>

              <span v-if="internalValue.location.cityStatePostalCode">
                {{ internalValue.location.cityStatePostalCode }}
              </span>
            </template>
          </div>
        </div>
      </div>
    </template>

    <template #icons>
      <RygenButton
        v-if="isFiconLocation || hasUserInput"
        mode="tertiary"
        :label="t('orders.clearAndStartOver')"
        @click="onClear"
      />

      <RygenButton
        mode="tertiary"
        :label="
          isFiconLocation ? t('common.changes') : t('address.addressBook')
        "
        @click="isShowingAddressBook = true"
      />
    </template>

    <template #default>
      <QuickQuoteStopEditContent
        :id="`${id}-content`"
        v-model="internalValue"
        :has-attempted-resolution="hasAttemptedResolution"
        @address-book-select="onShippingPointSelect"
        @clear="onClear"
      />

      <AddressBookModal
        v-model:visible="isShowingAddressBook"
        v-model:search-criteria="searchCriteria"
        :header="
          t('common.addParam', {
            param: isPickup ? t('orders.origin') : t('orders.destination'),
          })
        "
        :search-results="searchResults"
        :type="isPickup ? 'pickup' : 'dropoff'"
        :data-testid="`quick-quote-stop-edit-address-book-modal-${internalValue.sequenceNumber}`"
        @select="onShippingPointSelect"
      />
    </template>
  </Panel>
</template>

<script lang="ts" setup>
import { computed, nextTick, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import authorization from '@rygen/rygen-ui/authorization';
import { useModelValue, useValidations } from '@rygen/rygen-ui/composables';
import { Location, ShippingPointDetails } from '@rygen/rygen-ui/model';

import { useShippingPoints } from '@/composables';
import {
  EmbeddableLocation,
  QuickQuoteShippingPoint,
  ShipmentStopType,
} from '@/model';
import { useCurrentUserStore } from '@/stores/CurrentUser';
import { generateLocationLink } from '@/util';

import QuickQuoteStopEditContent from '@/components/quickQuote/QuickQuoteStopEditContent.vue';
import DropoffIcon from '@/components/shippingPoint/DropoffIcon.vue';
import PickupIcon from '@/components/shippingPoint/PickupIcon.vue';
import AddressBookModal from '@rygen/rygen-ui/components/address/AddressBookModal.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import Panel from 'primevue/panel';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  modelValue: {
    type: QuickQuoteShippingPoint,
    default: () => new QuickQuoteShippingPoint(),
  },
  hasAttemptedResolution: {
    type: Boolean,
    default: false,
  },
});
const { id, modelValue, hasAttemptedResolution } = toRefs(props);
const emit = defineEmits<{
  (event: 'update:model-value', modelValue: QuickQuoteShippingPoint): void;
}>();

const { t } = useI18n();
const { selectedDomain } = useCurrentUserStore();
const { internalValue } = useModelValue(modelValue, {
  emit,
  factoryMethod: (opts: Partial<QuickQuoteShippingPoint>) =>
    new QuickQuoteShippingPoint(opts),
});
const { resetSearch, searchCriteria, searchResults } = useShippingPoints();
const { v$ } = useValidations(internalValue, {});

const isShowingAddressBook = ref(false);

const isPickup = computed(
  () => internalValue.value.stopType === ShipmentStopType.PICKUP,
);

const hasLocationUpdate = computed(() =>
  authorization.hasAuthority('location', 'update'),
);

const isFiconLocation = computed(
  () => !!internalValue.value.location.locationId,
);

const hasUserInput = computed(() => {
  const location = internalValue.value.location;
  return (
    !!location.city ||
    !!location.state ||
    !!location.postalCode ||
    !!location.locationId ||
    internalValue.value.requestedAccessorials.length > 0
  );
});

const onClear = () => {
  internalValue.value = new QuickQuoteShippingPoint({
    stopType: internalValue.value.stopType,
    location: new EmbeddableLocation(),
    shipDate: new Date(),
  });
  nextTick(() => v$.value.$reset());
};

const onShippingPointSelect = (location: Location) => {
  internalValue.value.location = EmbeddableLocation.fromLocation(location);
  internalValue.value.shippingPointDetails = new ShippingPointDetails(
    location.shippingPointDetails,
  );
  setDefaultAccessorials(internalValue.value.shippingPointDetails);
};

const setDefaultAccessorials = (shippingPointDetails: ShippingPointDetails) => {
  const defaultAccessorials = shippingPointDetails.defaultAccessorials;
  const existingAccessorials = internalValue.value.requestedAccessorials.filter(
    (existingAcc) => {
      const isDefault = defaultAccessorials.some(
        (defaultAcc) => defaultAcc.naturalId === existingAcc.naturalId,
      );
      return !isDefault;
    },
  );

  internalValue.value.requestedAccessorials = [
    ...defaultAccessorials,
    ...existingAccessorials,
  ];
};

watch(isShowingAddressBook, (_newVal: boolean) => {
  resetSearch();
});
</script>

<style>
.quick-quote-stop-edit.p-panel {
  --p-panel-border-radius: var(--p-border-radius-sm);
  --p-panel-header-background: white;
  --p-panel-header-color: var(--p-gray-600);
  --p-panel-header-border-color: var(--p-content-border-color)
    var(--p-content-border-color) var(--divider-color)
    var(--stop-type-accent-color);
  --p-panel-header-border-radius: var(--p-border-radius-sm)
    var(--p-border-radius-sm) 0 0;
  --p-panel-header-border-width: 0 0 1px var(--sp-1);
  --p-panel-header-padding: 0 var(--sp-6) 0 0;
  --p-panel-content-padding: var(--sp-3) 0;

  min-width: 600px;

  .p-panel-header {
    font-size: 16px;
    font-weight: 500;

    .fa-layers {
      margin-right: var(--sp-1);
    }

    .shipment-stop-header {
      display: flex;

      .shipment-stop-header-label {
        display: flex;
        gap: var(--gap-1);
        align-items: center;
        border-right: var(--divider);
        padding: var(--sp-3) var(--sp-3) var(--sp-3) var(--sp-6);
      }

      .shipment-stop-header-address {
        display: flex;
        gap: var(--gap-1);
        justify-content: space-between;
        align-items: center;
        padding: var(--sp-3);
        width: 100%;

        .location-link {
          font-size: 14px;

          &:not(:hover) {
            text-decoration: none;
          }
        }
      }
    }
  }

  .p-panel-content {
    display: flex;
    flex-direction: column;
    gap: var(--gap-1);

    .row {
      display: flex;
      gap: var(--gap-1);
      padding: 0 var(--sp-6);
    }

    .border-top {
      border-top: var(--divider);
      padding-top: var(--sp-3);
    }
  }
}
</style>
